import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Predefined accent colors
export const ACCENT_COLORS = [
  { name: 'Blue', value: '#4f8cff', hover: '#3a7bff' },
  { name: 'Purple', value: '#8b5cf6', hover: '#7c3aed' },
  { name: 'Green', value: '#10b981', hover: '#059669' },
  { name: 'Orange', value: '#f59e0b', hover: '#d97706' },
  { name: 'Pink', value: '#ec4899', hover: '#db2777' },
  { name: 'Teal', value: '#14b8a6', hover: '#0d9488' },
];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  const [accentColor, setAccentColor] = useState(() => {
    const saved = localStorage.getItem('accentColor');
    return saved || ACCENT_COLORS[0].value;
  });

  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('viewMode');
    return saved || 'comfortable';
  });

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'light' ? '#f5f5f7' : '#1a1a2e');
    }
  }, [theme]);

  useEffect(() => {
    // Apply accent color
    const color = ACCENT_COLORS.find(c => c.value === accentColor) || ACCENT_COLORS[0];
    document.documentElement.style.setProperty('--accent', color.value);
    document.documentElement.style.setProperty('--accent-hover', color.hover);
    localStorage.setItem('accentColor', accentColor);
  }, [accentColor]);

  useEffect(() => {
    // Apply view mode
    document.documentElement.setAttribute('data-view-mode', viewMode);
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, toggleTheme,
      accentColor, setAccentColor,
      viewMode, setViewMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
